import { create } from 'zustand';
import type { AppNotification } from '../../sdk/@types/Notificacao';
import NotificationService from '../../sdk/services/NotificationService';
import ReviewService from '../../sdk/services/ReviewService';

/**
 * As duas leituras polled do sino — `GET /notifications` e `GET /review/stats`
 * — num estado só, **compartilhado por todos os consumidores montados na
 * mesma árvore**.
 *
 * Por que existe: `useNotifications` é montado pelo `NotificationBell` **e**
 * pelo `Sidebar` (e por quem mais precisar da fila), e `useNotifications`
 * consome `useDueCount`. Com estado por montagem, cada navegação fazia duas
 * `GET /notifications` e duas `GET /review/stats` — uma por consumidor — e o
 * `markRead` só derrubava o badge de quem chamou: o outro badge ficava com a
 * contagem velha até a navegação seguinte. Aqui a leitura é uma e o estado é um.
 *
 * A fila de moderação (`GET /moderation/stats`) **não mora aqui** — ela vive
 * em `useModerationCountersStore`, um store separado, de propósito: ao
 * contrário de notificações e revisão, cada instância de `useNotifications`
 * busca a fila por conta própria (não deduplicada — ver o teste
 * "o mesmo contador em toda instância montada" em `useNotifications.test.ts`),
 * e é só o *resultado* que é compartilhado, para que `applyModerationDelta`
 * derrube o sino e o Sidebar juntos sem round-trip. Misturar as duas fontes
 * neste store faria a chave `session|pathname|tick` deduplicar também a
 * moderação, e duas instâncias passariam a disputar uma única leitura —
 * quebrando exatamente o cenário que `useModerationCountersStore` existe
 * para resolver.
 *
 * Duas propriedades que o desenho precisa ter ao mesmo tempo, e que explicam
 * cada peça abaixo:
 *
 * 1. **Uma leitura por fonte por navegação, não uma por consumidor.** A chave
 *    de leitura (`session|pathname[|tick]`) é gravada **antes** da requisição
 *    sair, de forma síncrona: o segundo consumidor a montar no mesmo commit
 *    encontra a chave já gravada e não dispara nada. Isso também cobre o
 *    `React.StrictMode` do dev, que monta cada efeito duas vezes — o segundo
 *    `create` acha a chave da primeira.
 * 2. **Nada sobrevive à troca de usuário, e nada é persistido.** O estado é de
 *    módulo, mas está etiquetado com a sessão (o token) e é zerado assim que a
 *    etiqueta não bate — o "Sair" do Sidebar só navega para `/login`, sem
 *    limpar o storage nem recarregar o documento, então um segundo login
 *    acontece com esta árvore montada. Não há semente de `localStorage` aqui
 *    (ao contrário do `useProfileStore`, cuja semente é deliberada): o que este
 *    store guarda é conteúdo de um usuário.
 *
 * A vida do estado é a da árvore: quando o último consumidor desmonta, o store
 * volta ao zero (ver `acquireNotificationReads`), então uma montagem nova relê
 * mesmo que a rota seja a mesma de antes.
 */

/** As duas fontes deduplicadas por este store. */
export type ReadSource = 'notifications' | 'due';

interface NotificationReads {
  /**
   * O token sob o qual `items`/`unread`/`due` foram lidos. Quem consome
   * compara com o token de agora **durante o render**: assim a troca de
   * usuário não chega a pintar um frame com os dados do anterior.
   */
  session: string | null;
  items: AppNotification[];
  /** `unread` **da API**, nunca `items.length` — a lista pode vir filtrada. */
  unread: number;
  /** `dueToday` de `GET /review/stats`. */
  due: number;
  /**
   * A chave já pedida por fonte (`null` = ainda não pedida nesta sessão). É o
   * que deduplica: gravada antes da requisição sair.
   */
  keys: Record<ReadSource, string | null>;
  loadingNotifications: boolean;
  /** Primeiro erro do ciclo — o segundo não o mascara. */
  error: Error | null;
  /** Incrementado por `reload()`; entra na chave de notificações. */
  tick: number;
}

const BLANK: Omit<NotificationReads, 'session' | 'tick'> = {
  items: [],
  unread: 0,
  due: 0,
  keys: { notifications: null, due: null },
  loadingNotifications: false,
  error: null,
};

export const useNotificationReadsStore = create<NotificationReads>(() => ({
  session: null,
  tick: 0,
  ...BLANK,
}));

const { getState, setState } = useNotificationReadsStore;

/**
 * Invalida as respostas em voo. Uma requisição disparada antes de um reset (ou
 * de uma troca de sessão) não pode escrever no estado de depois dela.
 */
let generation = 0;

/** Consumidores montados. Zero = a árvore saiu; o estado não deve sobreviver. */
let consumers = 0;

/** Referência estável para a lista vazia — evita um `[]` novo por render. */
export const EMPTY_ITEMS: AppNotification[] = [];

/** Preserva o erro original (o interceptor pendura `apiError` nele). */
function toError(raw: unknown): Error {
  return raw instanceof Error ? raw : new Error(String(raw));
}

function reset(session: string | null) {
  generation += 1;
  setState({ session, ...BLANK });
}

/**
 * Registra um consumidor e devolve o `release`. Quando o último solta, o estado
 * volta ao zero — mas **num microtask**, não na hora: o `React.StrictMode` do
 * dev desmonta e remonta o mesmo efeito de forma síncrona, e uma navegação
 * troca o `DefaultLayout` inteiro (cada view monta o seu) no mesmo commit.
 * Zerar na hora faria as duas coisas relerem sem necessidade — e piscarem o
 * badge no meio de cada navegação.
 */
export function acquireNotificationReads(): () => void {
  consumers += 1;
  return () => {
    consumers -= 1;
    if (consumers > 0) return;
    queueMicrotask(() => {
      if (consumers === 0) reset(null);
    });
  };
}

/** A chave de uma leitura. `due` não leva `tick`: `reload()` não a relê. */
function readKey(source: ReadSource, session: string, pathname: string, tick: number) {
  return source === 'due' ? `${session} | ${pathname}` : `${session} | ${pathname} | ${tick}`;
}

/**
 * Dispara a leitura de uma fonte, uma vez por chave.
 *
 * `run` recebe `alive()`: falso quando a resposta chegou tarde demais — outra
 * sessão, outra rota, ou o estado zerado no meio do caminho. Nada do que chega
 * morto é escrito.
 */
function ensure(source: ReadSource, key: string, run: (alive: () => boolean) => void) {
  const { keys } = getState();
  if (keys[source] === key) return;

  setState({ keys: { ...keys, [source]: key } });

  const born = generation;
  run(() => born === generation && getState().keys[source] === key);
}

function fail(raw: unknown) {
  setState((state) => ({ error: state.error ?? toError(raw) }));
}

/** Troca de usuário na mesma aba: o que está em memória é do anterior. */
function adoptSession(session: string) {
  if (getState().session !== session) reset(session);
}

/**
 * `GET /review/stats` — a contagem de cartões vencendo hoje, uma vez por
 * sessão/rota. Desde `app-shell-pencil-fidelity` ela não soma mais no badge
 * do sino (vira a pill de revisão do Topbar e o bloco "Revisão" do painel);
 * mora aqui, e não num `useState` do `useDueCount`, porque o hook tem mais de
 * um chamador independente — cada consumidor de `useNotifications` e a pill
 * do Topbar — e sem isto cada um faria a sua própria leitura da mesma coisa.
 */
export function ensureDueRead(params: { session: string; pathname: string }) {
  const { session, pathname } = params;
  adoptSession(session);

  ensure('due', readKey('due', session, pathname, getState().tick), (alive) => {
    ReviewService.getStats()
      .then((stats) => {
        if (alive()) setState({ due: stats?.dueToday ?? 0 });
      })
      .catch(() => {
        // A revisão degrada para zero por conta própria — é a exceção
        // documentada em `useDueCount`, e continua sendo dela.
        if (alive()) setState({ due: 0 });
      });
  });
}

/** `GET /notifications`, uma vez por sessão/rota/`tick`. */
export function ensureNotificationReads(params: { session: string; pathname: string }) {
  const { session, pathname } = params;
  adoptSession(session);

  const { tick } = getState();

  ensure('notifications', readKey('notifications', session, pathname, tick), (alive) => {
    setState({ loadingNotifications: true, error: null });
    NotificationService.list()
      .then((list) => {
        if (!alive()) return;
        setState({ items: list.notifications, unread: list.unread });
      })
      .catch((raw) => {
        if (alive()) fail(raw);
      })
      .then(() => {
        if (alive()) setState({ loadingNotifications: false });
      });
  });
}

/** Escreve a lista e a contagem vindas do `markRead` — visíveis a todos. */
export function applyNotificationsPatch(patch: { items?: AppNotification[]; unread?: number }) {
  setState(patch);
}

/** `reload()`: relê notificações de todos os consumidores. */
export function bumpNotificationReads() {
  setState((state) => ({ tick: state.tick + 1 }));
}

/**
 * Zera tudo, na hora.
 *
 * Em produção ninguém precisa disto: a troca de sessão e a saída do último
 * consumidor já zeram. Existe para **teste**: um `renderHook` que desmonta cai
 * no reset adiado por microtask, e depender dessa ordem entre um teste e o
 * seguinte é frágil. Chame isto no `beforeEach` e o estado de um caso não
 * chega no outro.
 */
export function resetNotificationReads() {
  reset(null);
}
